import React, { useState } from 'react';
import { Station, ProjectConfig } from '../../types';
import { useStationForm } from '../../hooks/useStationForm';
import { useModalClose } from '../../hooks/useModalClose';
import { LatLongTooltip } from '../common/LatLongTooltip';
import { URLManager } from '../../lib/url/URLManager';
import { closeMenuAndUpdateUrl } from '../../lib/url/menuUtils';

interface Props {
  stations: Station[];
  onUpdate: (stationId: string, updates: Partial<Station>) => void;
  onClose: () => void;
  isOpen: boolean;
  project?: { identifier: string; config: ProjectConfig };
}

const LoadingSpinner = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <style>
      {`.spinner_qM83{animation:spinner_8HQG 1.05s infinite}.spinner_oXPr{animation-delay:.1s}.spinner_ZTLf{animation-delay:.2s}@keyframes spinner_8HQG{0%,57.14%{animation-timing-function:cubic-bezier(0.33,.66,.66,1);transform:translate(0)}28.57%{animation-timing-function:cubic-bezier(0.33,0,.66,.33);transform:translateY(-6px)}100%{transform:translate(0)}}`}
    </style>
    <circle className="spinner_qM83" cx="4" cy="12" r="3" fill="#666"/>
    <circle className="spinner_qM83 spinner_oXPr" cx="12" cy="12" r="3" fill="#666"/>
    <circle className="spinner_qM83 spinner_ZTLf" cx="20" cy="12" r="3" fill="#666"/>
  </svg>
);

export const CitiesMenu: React.FC<Props> = ({ stations, onUpdate, onClose, isOpen, project }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const urlManager = React.useRef(URLManager.getInstance());
  const modalRef = useModalClose({ 
    onClose: () => {
      if (project) {
        closeMenuAndUpdateUrl(project.identifier);
      }
      onClose();
    }, 
    project,
    view: 'cities'
  });

  // Update URL when menu opens
  React.useEffect(() => {
    if (isOpen && project) {
      urlManager.current.updateURL(
        project.identifier,
        'network',
        'cities',
        'auto',
        'cities_menu_open'
      );
    }
  }, [isOpen, project]);

  // Filter stations based on search term
  const filteredStations = stations.filter(station =>
    station.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Global actions
  const handleSetAllManual = () => {
    filteredStations.forEach(station => {
      onUpdate(station.id, { population_source: 'manual' });
    });
  };

  const handleSetAllAutomatic = () => {
    filteredStations.forEach(station => {
      onUpdate(station.id, { population_source: 'automatic' });
    });
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? 'open' : ''}`}>
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-2">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">City population</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search and Global Actions */}
        <div className="p-4 border-b">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <input
              type="text"
              placeholder="Search cities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border rounded"
            />
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleSetAllManual}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium w-full sm:w-[180px]"
              >
                Set all to manual
              </button>
              <button
                onClick={handleSetAllAutomatic}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium w-full sm:w-[180px]"
              >
                Set all to automatic
              </button>
            </div>
          </div>
        </div>

        {/* Station List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid gap-4">
            {filteredStations.map(station => (
              <StationCard
                key={station.id}
                station={station}
                onUpdate={(updates) => onUpdate(station.id, updates)}
                config={project?.config}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

interface StationCardProps {
  station: Station;
  onUpdate: (updates: Partial<Station>) => void;
  config?: ProjectConfig;
}

const StationCard = ({ station, onUpdate, config }: StationCardProps) => {
  if (!config) {
    console.error('Config is required for StationCard');
    return null;
  }

  const {
    name,
    setName,
    population,
    latitude,
    longitude,
    isLoading,
    populationSource,
    latitudeError,
    longitudeError,
    handlePopulationChange,
    handleCoordinateInput,
    handleSaveAndClose,
    handlePopulationSourceChange,
    savePopulation
  } = useStationForm({
    station,
    onUpdate,
    config
  });

  // Store form state in a ref for the useModalClose hook to access
  const formRef = React.useRef<HTMLFormElement>(null);
  React.useEffect(() => {
    if (formRef.current) {
      formRef.current.__formState = {
        name,
        population,
        latitude: latitude?.toString() || '',
        longitude: longitude?.toString() || '',
        populationSource
      };
    }
  }, [name, population, latitude, longitude, populationSource]);

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 station-card" data-station-id={station.id}>
      <form ref={formRef} onSubmit={(e) => {
        e.preventDefault();
        handleSaveAndClose(false);
      }}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-8">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              if (name !== station.name) {
                onUpdate({ name });
              }
            }}
            className="w-full sm:w-[55%] px-3 py-1.5 bg-white border border-gray-200 rounded-md text-lg font-medium"
          />
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={(e) => {
                e.preventDefault();
                handlePopulationSourceChange('manual');
              }}
              className={`flex-1 sm:flex-none px-3 h-[42px] rounded-md text-sm sm:w-[120px] ${
                populationSource === 'manual'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Manual
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                handlePopulationSourceChange('automatic');
              }}
              className={`flex-1 sm:flex-none px-3 h-[42px] rounded-md text-sm sm:w-[120px] ${
                populationSource === 'automatic'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Automatic
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Manual population</label>
            <input
              type="text"
              value={population}
              onChange={(e) => handlePopulationChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  savePopulation();
                  handleSaveAndClose(false);
                }
              }}
              onBlur={() => {
                savePopulation();
              }}
              className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              <div className="flex items-center gap-1 flex-wrap">
                <span>Automatic population: Latitude (open <a
                  href={`https://www.google.com/maps/?q=${encodeURIComponent(name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  Google Maps
                </a>)</span>
                <LatLongTooltip
                  content={
                    <>
                      Enter a value between -90 and 90. Accepted formats:
                      <ul className="mt-2 list-disc list-inside">
                        <li>Single value (e.g. <code>48.8588255</code>)</li>
                        <li>Comma-separated pair (e.g. <code>48.8588255,2.2646344</code>)</li>
                        <li>Comma-separated pair with additional data (e.g. <code>48.8588255,2.2646344,24664m</code>)</li>
                      </ul>
                    </>
                  }
                />
              </div>
            </label>
            <input
              type="text"
              value={latitude === null ? '' : latitude}
              onChange={(e) => {
                handleCoordinateInput(e.target.value, 'latitude');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAndClose();
                }
              }}
              onBlur={() => {
                if (latitude !== station.latitude || longitude !== station.longitude) {
                  handleSaveAndClose(false);
                }
              }}
              className={`w-full px-3 py-1.5 bg-white border rounded-md text-sm ${
                latitudeError ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">
              <div className="flex items-center gap-1 flex-wrap">
                <span>Automatic population: Longitude</span>
                <LatLongTooltip
                  content={
                    <>
                      Enter a value between -180 and 180. Accepted formats:
                      <ul className="mt-2 list-disc list-inside">
                        <li>Single value (e.g. <code>2.2646344</code>)</li>
                        <li>Comma-separated pair (e.g. <code>48.8588255,2.2646344</code>)</li>
                        <li>Comma-separated pair with additional data (e.g. <code>48.8588255,2.2646344,24664m</code>)</li>
                      </ul>
                    </>
                  }
                />
              </div>
            </label>
            <input
              type="text"
              value={longitude === null ? '' : longitude}
              onChange={(e) => {
                handleCoordinateInput(e.target.value, 'longitude');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveAndClose();
                }
              }}
              onBlur={() => {
                if (latitude !== station.latitude || longitude !== station.longitude) {
                  handleSaveAndClose(false);
                }
              }}
              className={`w-full px-3 py-1.5 bg-white border rounded-md text-sm ${
                longitudeError ? 'border-red-500 bg-red-50' : 'border-gray-200'
              }`}
            />
          </div>

          {station.circle_population !== null && (
            <div className="text-sm text-gray-500">
              Automatically calculated population within a {station.circle_population_radius}km radius: {new Intl.NumberFormat('en-US').format(station.circle_population || 0)}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}; 